import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Boxes, Hammer, Plus, RefreshCw, Search } from 'lucide-react';
import StaffScanGate from '@/components/StaffScanGate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { EwbDocPanel } from '@/components/ewayBill/EwbDocPanel';
import { NewJwRequestDialog } from '@/components/tissue/NewJwRequestDialog';
import { PackStockTable } from '@/components/tissue/PackStockTable';
import { JwStatusBadge, fmtDate, sortSummary } from '@/components/tissue/JwStatusBadge';
import { listOrders } from '@/services/tissueJwApi';
import { JwEwbCell, JwTransferPrintBtn, useJwEwb } from '@/components/jobWork/JwTransfer';

const FILTERS = {
  active: 'REQUESTED,PICKING,TRANSFERRED,AT_JOBBER,RECEIVING',
  closed: 'COMPLETED',
  other: 'REJECTED,CANCELLED',
  all: '',
};

const OrdersTable = ({ orders, onOpen, ewb }) => (
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead>JW ID</TableHead><TableHead>Jobber</TableHead><TableHead>Sort × pieces</TableHead>
        <TableHead className="text-right">Sent</TableHead><TableHead className="text-right">Received</TableHead>
        <TableHead>Status</TableHead><TableHead>E-way bill</TableHead><TableHead>Raised</TableHead><TableHead />
      </TableRow>
    </TableHeader>
    <TableBody>
      {!orders.length && <TableRow><TableCell colSpan={9} className="text-center text-gray-400 py-8">No job orders here</TableCell></TableRow>}
      {orders.map((o) => (
        <TableRow key={o.jw_id} className="cursor-pointer hover:bg-amber-50/60" onClick={() => onOpen(o.jw_id)} data-testid={`tjw-row-${o.jw_id}`}>
          <TableCell className="font-mono text-xs">{o.jw_id}</TableCell>
          <TableCell className="text-sm">{o.jobber_name}</TableCell>
          <TableCell className="font-mono text-xs">{sortSummary(o)}</TableCell>
          <TableCell className="text-right">{o.sent_pcs || 0}</TableCell>
          <TableCell className="text-right">{o.received_pcs || 0}<span className="text-[11px] text-gray-400"> / {o.received_packs || 0} packs</span></TableCell>
          <TableCell><JwStatusBadge status={o.status} testId={`tjw-status-${o.jw_id}`} /></TableCell>
          <TableCell><JwEwbCell s={ewb[o.jw_id]} refNo={o.jw_id} testId={`tjw-ewb-${o.jw_id}`} /></TableCell>
          <TableCell className="text-xs text-gray-500">{fmtDate(o.created_at)}</TableCell>
          <TableCell className="text-right whitespace-nowrap space-x-1">
            <JwTransferPrintBtn kind="job_order" refNo={o.jw_id} s={ewb[o.jw_id]} label="Transfer" testId={`tjw-transfer-print-${o.jw_id}`} />
            <Button size="sm" variant="outline" data-testid={`tjw-open-${o.jw_id}`}>Open</Button></TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const TissueHub = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('jw-orders');
  const [filter, setFilter] = useState('active');
  const [q, setQ] = useState('');
  const [orders, setOrders] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [ewb] = useJwEwb('job_order', orders.map((o) => o.jw_id));

  const load = () => listOrders({ status: FILTERS[filter] || undefined, q: q || undefined })
    .then((r) => setOrders(r.orders || [])).catch(() => toast.error('Could not load job orders'));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <StaffScanGate stationName="Tissue Section — Job Work">
      <div className="min-h-screen bg-[#FFFBF5]" data-testid="tissue-hub">
        <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
          <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/sections/tissue')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="tissue-hub-back">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold font-display">Tissue — Job Work</h1>
                <p className="text-sm text-[#E8DCC8]">Greige sort bundles out to the jobber → FG packs back</p>
              </div>
            </div>
            <Button onClick={() => setShowNew(true)} className="bg-[#C9A961] hover:bg-[#B8964F] text-[#1A1F2E]" data-testid="new-jw-request-btn">
              <Plus className="w-4 h-4 mr-2" /> New request
            </Button>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="jw-orders" data-testid="tab-jw-orders"><Hammer className="w-4 h-4 mr-1" /> Job orders</TabsTrigger>
              <TabsTrigger value="pack-stock" data-testid="tab-pack-stock"><Boxes className="w-4 h-4 mr-1" /> Pack stock</TabsTrigger>
            </TabsList>
            <TabsContent value="jw-orders" className="space-y-4">
              <div className="flex flex-wrap gap-2 items-center">
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="w-[180px]" data-testid="tjw-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Open orders</SelectItem><SelectItem value="closed">Closed</SelectItem>
                    <SelectItem value="other">Rejected / cancelled</SelectItem><SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
                <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2">
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="JW ID, jobber or sort no" className="w-[260px]" data-testid="tjw-search" />
                  <Button type="submit" variant="outline" data-testid="tjw-search-btn"><Search className="w-4 h-4" /></Button>
                </form>
                <Button variant="ghost" onClick={load} data-testid="tjw-refresh"><RefreshCw className="w-4 h-4" /></Button>
              </div>
              <Card><CardContent className="pt-6"><OrdersTable orders={orders} ewb={ewb} onOpen={(id) => navigate(`/sections/tissue/jw/${id}`)} /></CardContent></Card>
              <EwbDocPanel docType="job_work" section="Tissue" title="Job order e-way bills" />
            </TabsContent>
            <TabsContent value="pack-stock"><PackStockTable /></TabsContent>
          </Tabs>
        </main>
        <NewJwRequestDialog open={showNew} onOpenChange={setShowNew} onCreated={load} />
      </div>
    </StaffScanGate>
  );
};

export default TissueHub;
